import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CreditCard, Plus, User, Building2, Mail, Check } from 'lucide-react';
import SignInModal from '../components/SignInModal';
import { Button } from '../components/ui/button';

const NFCClaim = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [showSignIn, setShowSignIn] = useState(false);
  const [availableCards, setAvailableCards] = useState([]);
  const [showCardSelection, setShowCardSelection] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [linking, setLinking] = useState(false);

  // Redirect to create page with NFC code stored
  const redirectToCreate = useCallback(() => {
    localStorage.setItem('nfc_claim_code', code);
    toast({
      title: "NFC Card Ready",
      description: "Create your digital business card now!"
    });
    navigate('/create');
  }, [code, toast, navigate]);

  // Check for existing unlinked cards
  const checkExistingCards = useCallback(async () => {
    if (!user) return;
    
    console.log('checkExistingCards called');
    try {
      const token = await user.getIdToken();
      console.log('Got token, fetching available cards...');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nfc/${code}/available-cards`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Available cards response:', data);
        if (data.has_cards && data.available_cards.length > 0) {
          // User has existing cards - show selection
          console.log('Setting available cards:', data.available_cards);
          setAvailableCards(data.available_cards);
          setShowCardSelection(true);
          setLoading(false);
        } else {
          // No existing cards - redirect to create
          console.log('No available cards, redirecting to create');
          redirectToCreate();
        }
      } else if (response.status === 404) {
        // NFC code not found - show error
        const errorData = await response.json();
        console.error('NFC code not found:', errorData);
        toast({
          title: "Invalid QR Code",
          description: "This QR code is not valid or has expired.",
          variant: "destructive"
        });
        navigate('/');
      } else {
        // Other error - try to create anyway
        console.error('Error fetching available cards, status:', response.status);
        redirectToCreate();
      }
    } catch (error) {
      console.error('Error checking existing cards:', error);
      redirectToCreate();
    }
  }, [user, code, toast, navigate, redirectToCreate]);

  // Check QR code status on load
  useEffect(() => {
    const checkQRStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nfc/${code}`);
        const data = await response.json();
        
        if (!response.ok) {
          toast({
            title: "Invalid QR Code",
            description: "This QR code is not valid.",
            variant: "destructive"
          });
          navigate('/');
          return;
        }
        
        // If already claimed, redirect to bio page immediately
        if (data.status === 'claimed' && data.redirect_to) {
          navigate(data.redirect_to);
          return;
        }
        
        // If unclaimed
        if (data.status === 'unclaimed') {
          if (!user) {
            // Not logged in - show sign-in modal
            setShowSignIn(true);
            setLoading(false);
          } else {
            // Logged in - check for existing cards
            console.log('User is logged in, checking for existing cards...');
            await checkExistingCards();
          }
        }
        
      } catch (error) {
        console.error('Error checking QR status:', error);
        toast({
          title: "Error",
          description: "Failed to verify QR code",
          variant: "destructive"
        });
        navigate('/');
      }
    };
    
    if (code && !authLoading) {
      checkQRStatus();
    }
  }, [code, navigate, toast, user, authLoading, checkExistingCards]);

  // Link selected card to NFC code
  const handleLinkCard = async (card) => {
    setLinking(true);
    setSelectedCard(card.id);
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nfc/${code}/link`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            card_id: card.id,
            username: card.username
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Card Linked Successfully!",
          description: `Your card "${card.name}" is now linked to this NFC code.`
        });
        // Redirect to bio page
        navigate(data.redirect_to || `/bio/${card.username}`);
      } else {
        throw new Error(data.detail || 'Failed to link card');
      }
    } catch (error) {
      console.error('Error linking card:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to link card to NFC code",
        variant: "destructive"
      });
      setSelectedCard(null);
    } finally {
      setLinking(false);
    }
  };

  // Handle post-login redirect
  useEffect(() => {
    if (!authLoading && user && showSignIn) {
      // User just logged in - check for existing cards
      setShowSignIn(false);
      checkExistingCards();
    }
  }, [user, authLoading, showSignIn, checkExistingCards]);

  // Handle sign-in modal close
  const handleSignInClose = (open) => {
    setShowSignIn(open);
    // If user closes modal without signing in, redirect to home
    if (!open && !user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to claim your NFC card",
        variant: "default"
      });
      navigate('/');
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">{authLoading ? 'Checking authentication...' : 'Verifying QR code...'}</p>
        </div>
      </div>
    );
  }

  // Show card selection UI
  if (showCardSelection && availableCards.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center gap-2">
            <img src="/logo.png" alt="Piper Card" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold text-gray-900">Piper Card</span>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Your Card</h1>
              <p className="text-gray-600">
                Choose an existing card to link to this NFC code, or create a new one.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                NFC Code: <span className="font-mono font-medium text-amber-600">{code?.toUpperCase()}</span>
              </p>
            </div>

            {/* Existing Cards List */}
            <div className="space-y-3 mb-6">
              <p className="text-sm font-medium text-gray-700">Your Available Cards:</p>
              {availableCards.map((card) => (
                <div
                  key={card.id}
                  onClick={() => !linking && handleLinkCard(card)}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    selectedCard === card.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                  } ${linking ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`card-option-${card.username}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Profile Image or Placeholder */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {card.profileImage ? (
                        <img 
                          src={card.profileImage} 
                          alt={card.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-white" />
                      )}
                    </div>
                    
                    {/* Card Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{card.name}</h3>
                      {card.title && (
                        <p className="text-sm text-gray-600 truncate flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {card.title}{card.company && ` at ${card.company}`}
                        </p>
                      )}
                      {card.email && (
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {card.email}
                        </p>
                      )}
                    </div>

                    {/* Selection indicator */}
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedCard === card.id 
                        ? 'border-amber-500 bg-amber-500' 
                        : 'border-gray-300'
                    }`}>
                      {selectedCard === card.id && (
                        linking ? (
                          <Loader2 className="w-4 h-4 text-white animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 text-white" />
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Create New Card Button */}
            <Button
              onClick={redirectToCreate}
              variant="outline"
              className="w-full py-6 border-2 border-dashed border-gray-300 hover:border-amber-400 hover:bg-amber-50"
              disabled={linking}
              data-testid="create-new-card-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create a New Card
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center gap-2">
          <img src="/logo.png" alt="Piper Card" className="w-8 h-8 object-contain" />
          <span className="text-xl font-bold text-gray-900">Piper Card</span>
        </div>
      </header>

      {/* Main Content - Loading/Redirect State */}
      <main className="max-w-lg mx-auto px-4 py-16 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
        <p className="text-gray-600">Processing your NFC card...</p>
        <p className="text-sm text-gray-500 mt-2">
          QR Code: <span className="font-mono font-medium">{code?.toUpperCase()}</span>
        </p>
      </main>

      {/* Sign In Modal */}
      <SignInModal 
        open={showSignIn} 
        onOpenChange={handleSignInClose}
        redirectPath={`/c/${code}`}
      />
    </div>
  );
};

export default NFCClaim;
